import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getGlobalSearchExport, makeEnhancedSearchAdvanced } from '../../actions/SearchPageActions';
import ExportIcon from '../../components/SavedSearchesTable/ExportIcon';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getAppOptionByKey } from '../../selectors/AppConfigsSelectors';

const getExportParameters = (searchPage) => ({
  entityNames: searchPage.entityNames?.filter((e) => e !== 'ALL' && e !== 'all' && e !== ''),
  sortExpression: searchPage.sortExpression,
  searchText: searchPage.searchText,
  searchOption: searchPage.searchOption,
  getCrossReferences: searchPage.getCrossReferences,
  advancedSearchCriteria: searchPage.advancedSearchCriteria?.length
    ? searchPage.advancedSearchCriteria
    : [],
  useContainsTable: searchPage.useContainsTable,
});

const useStyles = makeStyles(({ palette }) => ({
  header: {
    display: 'flex',
    padding: '32px 24px',
    borderBottomColor: palette?.grey[300],
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 'normal',
    lineHeight: 1.5,
  },
  select: {
    width: 263,
  },
  exportButton: {
    width: '1.3rem',
  },
}));

const Header = ({ resultsCount, searchQuery, entityType }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const searchPage = useSelector((state) => state?.searchPage);
  const allowEnhancedGlobalSearch = useSelector(getAppOptionByKey('allowEnhancedGlobalSearch'));
  const [searchText, setSearchText] = useState(searchPage?.searchText || searchQuery);

  const exportParameters = useMemo(() => getExportParameters(searchPage), [searchPage]);

  useEffect(() => {
    setSearchText(searchPage?.searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPage?.searchText]);

  const handleExport = () => {
    if (exportParameters?.searchText?.trim() !== '') {
      dispatch(getGlobalSearchExport(exportParameters));
      dispatch(makeEnhancedSearchAdvanced({ hasError: false }));
    } else {
      dispatch(makeEnhancedSearchAdvanced({ hasError: true }));
    }
  };

  return (
    <div className={classes.header}>
      <div>
        <Typography className={classes.title} variant="h2" gutterBottom>
          {resultsCount} {translateString('Search Results for')}{' '}
          <strong>{`“${searchText}”`}</strong>
        </Typography>
      </div>
      <div style={{ marginTop: 10 }}>
        {allowEnhancedGlobalSearch && (
          <>
            <Button
              color="primary"
              style={{ marginRight: 5 }}
              onClick={() =>
                dispatch(
                  makeEnhancedSearchAdvanced({
                    ...searchPage,
                    isAdvancedSearch: !searchPage?.isAdvancedSearch,
                    entityNames: entityType !== 'ALL' && entityType !== '' ? [entityType] : [],
                    searchText,
                  })
                )
              }
              size="small"
            >
              {translateString('Advanced Options')}
            </Button>
            <Tooltip title={translateString('Export')} placement="top">
              <IconButton
                style={{ marginRight: 5 }}
                size="small"
                onClick={handleExport}
                aria-label={translateString('Export')}
              >
                <ExportIcon className={classes.exportButton} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
